import { type DocumentPatch, invertPatch, isEmptyPatch, mergePatches } from './patch';

/**
 * The undo stack.
 *
 * Pure data and pure functions — the session below owns the document, this owns only the
 * log. Keeping them apart is what lets a drag preview run the whole edit pipeline and
 * throw the result away without touching history.
 */

export interface Command {
  readonly label: string;
  readonly patch: DocumentPatch;
  /**
   * Gesture identity.
   *
   * Two consecutive commands with the same non-empty key belong to one gesture and are
   * compressed into a single undo step. A drag emits a command per pointer-move; without
   * this, undoing a two-second drag would take four hundred presses.
   */
  readonly mergeKey?: string | undefined;
}

export interface History {
  readonly past: readonly Command[];
  readonly future: readonly Command[];
  /**
   * Whether the top of `past` may still absorb a matching command.
   *
   * Sealed when a gesture ends. Without it, dragging a point, releasing, and dragging the
   * same point again would merge into one undo step — the two gestures share a merge key
   * but are plainly two separate edits.
   */
  readonly open: boolean;
  readonly limit: number;
}

/** Entries kept before the oldest are discarded. */
const DEFAULT_LIMIT = 200;

export function createHistory(limit = DEFAULT_LIMIT): History {
  return { past: [], future: [], open: false, limit };
}

export function canUndo(history: History): boolean {
  return history.past.length > 0;
}

export function canRedo(history: History): boolean {
  return history.future.length > 0;
}

/** Label of the edit `undo` would reverse, for the menu item. */
export function undoLabel(history: History): string | null {
  return history.past[history.past.length - 1]?.label ?? null;
}

export function redoLabel(history: History): string | null {
  return history.future[history.future.length - 1]?.label ?? null;
}

/**
 * Records a command, merging it into the previous one when they form one gesture.
 *
 * Recording always clears the redo stack: once the user edits after undoing, the branch
 * they undid is unreachable and keeping it would let a later redo produce a document
 * neither state ever existed in.
 */
export function record(history: History, command: Command): History {
  if (isEmptyPatch(command.patch)) return history;

  const top = history.past[history.past.length - 1];

  if (
    history.open &&
    top !== undefined &&
    command.mergeKey !== undefined &&
    command.mergeKey !== '' &&
    top.mergeKey === command.mergeKey
  ) {
    const merged: Command = {
      label: top.label,
      patch: mergePatches(top.patch, command.patch),
      mergeKey: command.mergeKey,
    };
    return {
      ...history,
      past: [...history.past.slice(0, -1), merged],
      future: [],
      open: true,
    };
  }

  const past = [...history.past, command];
  return {
    ...history,
    past: past.length > history.limit ? past.slice(past.length - history.limit) : past,
    future: [],
    open: command.mergeKey !== undefined && command.mergeKey !== '',
  };
}

/** Ends a gesture, so the next command starts a new undo step. */
export function seal(history: History): History {
  return history.open ? { ...history, open: false } : history;
}

export interface HistoryStep {
  readonly history: History;
  /** The patch to apply to the document. */
  readonly patch: DocumentPatch;
  readonly label: string;
}

export function undo(history: History): HistoryStep | null {
  const command = history.past[history.past.length - 1];
  if (command === undefined) return null;

  return {
    history: {
      ...history,
      past: history.past.slice(0, -1),
      future: [...history.future, command],
      open: false,
    },
    patch: invertPatch(command.patch),
    label: command.label,
  };
}

export function redo(history: History): HistoryStep | null {
  const command = history.future[history.future.length - 1];
  if (command === undefined) return null;

  return {
    history: {
      ...history,
      past: [...history.past, command],
      future: history.future.slice(0, -1),
      open: false,
    },
    patch: command.patch,
    label: command.label,
  };
}

export function clearHistory(history: History): History {
  return { ...history, past: [], future: [], open: false };
}
